import { Trans, useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { MezzaFormField } from "@/components/core/Form/MezzaFormField";
import { MezzaOtpField } from "@/components/core/Form/MezzaOtpField";
import { ButtonWithLoading } from "@/components/core/Form/ButtonWithLoading";
import { cn } from "@/lib/utils";
import { OTP_CONFIG } from "@/consts/otp.consts";
import { useEmailOtpFlow, FlowStep } from "@/hooks/useEmailOtpFlow";

interface EmailOtpFlowProps {
  onMouseDown?: (e: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

export function EmailOtpFlow({ onMouseDown }: EmailOtpFlowProps) {
  const { t } = useTranslation();
  const {
    step,
    email,
    emailForm,
    otpForm,
    isRequestingOtp,
    isVerifyingOtp,
    isSigningIn,
    isCompleted,
    formattedTime,
    handleEmailSubmit,
    handleOtpSubmit,
    handleResend,
    handleBack,
  } = useEmailOtpFlow();

  const isLoading = isVerifyingOtp || isSigningIn;

  if (step === FlowStep.EMAIL) {
    return (
      <Form {...emailForm}>
        <form
          onSubmit={emailForm.handleSubmit(handleEmailSubmit)}
          className="space-y-6"
        >
          <div className="space-y-4">
            <MezzaFormField
              form={emailForm}
              name="email"
              placeholder="auth.email"
              type="email"
              inputMode="email"
              onMouseDown={onMouseDown}
            />
          </div>

          <ButtonWithLoading
            type="submit"
            className="w-full"
            disabled={isRequestingOtp}
            isLoading={isRequestingOtp}
          >
            {t("auth.sendVerificationCode")}
          </ButtonWithLoading>
        </form>
      </Form>
    );
  }

  // OTP verification step
  return (
    <Card className="w-full mx-auto border-none shadow-none bg-transparent">
      <CardHeader className="px-0">
        <div className="space-y-2">
          <h2 className="text-2xl font-bold">{t("verification.heading")}</h2>
          <p className="text-sm text-muted-foreground">
            {t("auth.enterVerificationCode")}
          </p>
        </div>
      </CardHeader>

      <CardContent className="px-0">
        <Form {...otpForm}>
          <form
            onSubmit={otpForm.handleSubmit(handleOtpSubmit)}
            className="flex flex-col space-y-6"
          >
            <MezzaOtpField
              form={otpForm}
              name="otp"
              label="verification.otpLabel"
              length={OTP_CONFIG.LENGTH}
              className="mx-auto"
              autoFocus
              onComplete={(value) => {
                otpForm.setValue("otp", value);
                otpForm.handleSubmit(handleOtpSubmit)();
              }}
            />

            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                <Trans
                  i18nKey="auth.verificationCodeSent"
                  values={{ email }}
                />
              </p>
            </div>

            <div className="text-center">
              {isCompleted ? (
                <ButtonWithLoading
                  type="button"
                  variant="link"
                  onClick={handleResend}
                  disabled={isRequestingOtp}
                  className={cn(
                    "p-0 h-auto",
                    isRequestingOtp && "!bg-transparent"
                  )}
                  isLoading={isRequestingOtp}
                >
                  {t("auth.resendCode")}
                </ButtonWithLoading>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t("auth.resendIn")} {formattedTime}
                </p>
              )}
            </div>

            <div className="flex flex-col space-y-3">
              <ButtonWithLoading
                type="submit"
                className="w-full"
                disabled={isLoading || isRequestingOtp}
                isLoading={isLoading}
              >
                {t("verification.submit_login")}
              </ButtonWithLoading>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleBack}
                disabled={isLoading || isRequestingOtp}
              >
                {t("common.back")}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}

